import { useEffect, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { createSelector } from "reselect";

import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import ActionButton from "components/Buttons/ActionButton";
import { EditButton } from "components/Buttons/EditButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import StatusToggle from "components/StatusToggle";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { toast } from "react-toastify";
import {
  fetchTeam,
  updateStatusInTeam as onUpdateStatusTeam,
  TeamExportsCSV,
} from "slices/team/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";

const TeamList = () => {
  document.title = "Teams List";
  const dispatch = useDispatch<any>();
  const location = useLocation();

  // Use the preserved table state hook
  const tableState = usePreservedTableState({
    key: "team",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue: colValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue: setColValue,
    resetState,
  } = tableState;

  const selectorganisation = createSelector(
    (state: any) => state.teams,
    (Teams) => ({
      teams: Teams.teamList, // API's data array for current page
      rowCount: Teams.rowCount, // API's total count
      loading: Teams.loading,
      error: Teams.error,
    })
  );

  const { teams, rowCount, loading, error } = useSelector(selectorganisation);

  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.user_management?.app_name,
    app_modules?.user_management?.modules?.team
  );

  // Fetch data from server on any table state change
  const getBackendSortKey = (key: string) => {
    const map: Record<string, string> = {
      department_name: "department_name",
      organization_name: "organization_name",
      team_name: "team_name",
      created_at: "created_at",
      updated_at: "updated_at",
    };
    return map[key] || key;
  };

  const sortField = sorting[0]?.id ? getBackendSortKey(sorting[0].id) : "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // Convert filterValue (filterField/filterValue) to ColumnFiltersState (id/value) format
  const convertedFilters = useMemo(() => {
    if (!colValue || !Array.isArray(colValue)) return [];
    return colValue.map((f: any) => ({
      id: f.filterField || f.id,
      value: f.filterValue !== undefined ? f.filterValue : f.value,
    }));
  }, [colValue]);

  useEffect(() => {
    dispatch(
      fetchTeam({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: convertedFilters,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    convertedFilters,
  ]);

  // Debounce search input to actual search
  useEffect(() => {
    // Skip if searchInput already matches search (prevents unnecessary updates during restoration)
    if (searchInput === search) {
      return;
    }

    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch, search]);

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // Handle status toggle
  const handleStatusToggle = async (org: any) => {
    // Prevent toggling status if sr_no is 1
    if (org.sr_no === 1) {
      toast.error("Cannot toggle status for this team.");
      return;
    }

    try {
      const updatedStatus = org.is_active === true ? false : true;
      const update_payload = {
        team_name: org?.team_name,
        department: org?.department,
        team_id: org?.team_id,
        is_active: updatedStatus,
      };
      console.log(update_payload, "update_payload");
      await dispatch(onUpdateStatusTeam(update_payload)).unwrap();
      toast.success(
        `Teams ${updatedStatus ? "activated" : "deactivated"} successfully`
      );
      dispatch(
        fetchTeam({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: convertedFilters,
        })
      );
    } catch (err: any) {
      console.error("Update Status Error:", err);
      toast.error(err?.message || "Failed to update status");
    }
  };

  // const handleExport = async () => {
  //   const resultAction = await dispatch(exportOTeamsCSV());
  //   if (exportOTeamsCSV.fulfilled.match(resultAction)) {
  //     const { blob, filename } = resultAction.payload;
  //     const url = window.URL.createObjectURL(new Blob([blob]));
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.setAttribute("download", filename);
  //     document.body.appendChild(link);
  //     link.click();
  //     link.parentNode?.removeChild(link);
  //      window.URL.revokeObjectURL(url)
  //     toast.success('CSV exported successfully')
  //   } else {
  //     toast.error("Failed to export CSV");
  //   }
  // };

  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const { blob, filename } = await TeamExportsCSV({
        search,
        filters: convertedFilters,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      console.error("Export failed", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export CSV";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };

  const DynamicAction = canUpdate
    ? [
        {
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => (
            <EditButton to={`/team-forms/${row?.original?.team_id}`} />
          ),
        },
      ]
    : [];
  // Table columns
  const columns = useMemo(
    () => [
      {
        header: "Sr. No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return row.original.sr_no || row.index + 1 + pageIndex * pageSize;
        },
      },
      {
        header: "Team",
        accessorKey: "team_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/teams/dropdown/team_name",
      },
      {
        id: "department.department_name",
        header: "Department",
        accessorKey: "department.department_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/teams/dropdown/department_name",
      },
      {
        id: "organization.organization_name",
        header: "Organisations",
        accessorKey: "organization.organization_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/teams/dropdown/organization_name",
      },

      {
        header: "Status",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const org = row.original;
          return (
            <StatusToggle
              isActive={org.is_active}
              onChange={() => {
                handleStatusToggle(org);
              }}
              disabled={!canUpdate || org.sr_no === 1}
            />
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username ?? "N/A";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username ?? "N/A";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      ...DynamicAction,
    ],
    [handleStatusToggle, canUpdate, DynamicAction, pageIndex, pageSize]
  );

  return (
    <ListingPageContainer>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Teams</h6>
                </Col>

                {/* Search Input Box (reusable)*/}
                <Col xl={2} md={3}>
                  <SearchBox value={searchInput} onChange={setSearchInput} />
                </Col>

                <Col className="col-md-auto">
                  <div className="hstack gap-2">
                    {canView && (
                      <ActionButton
                        label="Export"
                        icon="bi bi-box-arrow-up"
                        handleClick={handleExport}
                        variant="light"
                      />
                    )}
                    {canCreate && (
                      <ActionButton
                        label="Add Teams"
                        icon="bi bi-plus-circle"
                        navigateLink="/team-forms"
                        variant="primary"
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <TableContainer
                  columns={columns}
                  data={teams}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  colValue={setColValue}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  manualSorting={true}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search Items..."
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default TeamList;
